import { Alert, Box, CircularProgress, Divider, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const pdfOptions = {
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
};

const StyledDocument = styled(Document)(({ theme }) => ({
  width: "100%",
  height: theme.spacing(64),
  overflow: "auto",
}));

const Loading = () => (
  <Box sx={{ p: 4 }}>
    <CircularProgress size={32} />
  </Box>
);

export const PreviewFile = ({ file }: { file: File | string }) => {
  const [numberOfPages, setNumberOfPages] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "fileType",
      typeof file === "string"
        ? file
        : file.name + file.size + file.lastModified,
    ],
    queryFn: async () => {
      const detectTypeFromBytes = (bytes: Uint8Array): string | null => {
        if (
          bytes[0] === 0x89 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x4e &&
          bytes[3] === 0x47
        ) {
          return "image/png";
        }
        if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
          return "image/jpeg";
        }
        return null;
      };

      if (typeof file === "string") {
        const response = await fetch(file);
        if (!response.ok) {
          throw new Error(`Failed to fetch file`);
        }
        const contentType = response.headers.get("Content-Type");
        if (contentType === "application/pdf") {
          const blob = await response.blob();
          const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
          const detectedType = detectTypeFromBytes(header);
          if (detectedType) {
            const correctedBlob = new Blob([blob], { type: detectedType });
            return {
              type: detectedType,
              blobUrl: URL.createObjectURL(correctedBlob),
            };
          }
        }
        return { type: contentType };
      }
      if (file.type === "application/pdf") {
        const header = new Uint8Array(await file.slice(0, 4).arrayBuffer());
        const detectedType = detectTypeFromBytes(header);
        if (detectedType) {
          const correctedBlob = new Blob([file], { type: detectedType });
          return {
            type: detectedType,
            blobUrl: URL.createObjectURL(correctedBlob),
          };
        }
      }
      return { type: file.type };
    },
    enabled: typeof file === "string" || file.type === "application/pdf",
    initialData:
      typeof file === "string" || file.type === "application/pdf"
        ? undefined
        : { type: file.type },
  });

  const fileType = data?.type;
  const fileSrc =
    data?.blobUrl ??
    (typeof file === "string" ? file : URL.createObjectURL(file));

  if (fileType?.substring(0, 5) === "image") {
    return (
      <>
        {data?.blobUrl && (
          <Alert severity="warning">OBS: filen är en bildfil (ej pdf)</Alert>
        )}
        <Box
          component="img"
          src={fileSrc}
          sx={{
            width: "100%",
            height: ({ spacing }) => spacing(64),
            objectFit: "contain",
          }}
        />
      </>
    );
  }

  if (fileType === "application/pdf") {
    return (
      <PdfPreview
        file={typeof file === "string" ? file : URL.createObjectURL(file)}
        numberOfPages={numberOfPages}
        setNumberOfPages={setNumberOfPages}
      />
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return <Alert severity="info">Not supported format {fileType}</Alert>;
};

const PdfPreview = ({
  file,
  numberOfPages,
  setNumberOfPages,
}: {
  file: string;
  numberOfPages: number;
  setNumberOfPages: (n: number) => void;
}) => {
  return (
    <StyledDocument
      file={file}
      options={pdfOptions}
      loading={<Loading />}
      onLoadSuccess={({ numPages }) => setNumberOfPages(numPages)}
    >
      {Array.from({ length: numberOfPages }, (_, index) => (
        <Fragment key={index.toString()}>
          {!!index && <Divider />}
          <Box
            sx={{
              overflow: "hidden",
              "& .react-pdf__Page": { maxWidth: "100%", overflow: "hidden" },
              "& canvas": { maxWidth: "100%", height: "auto !important" },
            }}
          >
            <Page pageNumber={index + 1} />
          </Box>
        </Fragment>
      ))}
    </StyledDocument>
  );
};
